import { useCallback, useEffect, useMemo, useState } from 'react';
import Page from '../Page.js';
import PageContentWrapper from '../../components/PageContentWrapper.js';
import uiManager from '../../../config/ui.js';
import PAGES from '../../data/PagesConstants.js';
import FormPartial from './FormPartial.js';
import CsDiagnostics from '../../models/CsDiagnostics.js';
import { toast } from 'react-toastify';
import ShowInfo from './ShowInfo.js';
import Loading from '../../components/Loading.js';
import ShowSummaryData from './ShowSummaryData.js';
import {
  postMqttTrigger,
  fetchCsInformation,
  fetchCsLogCustomer,
  fetchUploadLog,
  postStartMetadata,
  postStopMetadata,
  fetchMetadata,
} from '../../api/csDiagnosticsApi.js';
import MqttConfirmModal from './components/MqttConfirmModal.js';
import './ChargingStationDiagnostics.scss';

const IndexForm = () => {
  const [csDiagnostics, setCsDiagnostics] = useState(new CsDiagnostics().toStateJson());
  const [csInformation, setCsInformation] = useState({});
  const [csLogCustomers, setCsLogCustomers] = useState([]);
  const [csLogData, setCsLogData] = useState([]);
  const [metadataRows, setMetadataRows] = useState([]);
  const [metadataColumns, setMetadataColumns] = useState([]);
  const [metadataPanelVisible, setMetadataPanelVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isApiCalled, setIsApiCalled] = useState(false);

  const searchKey = useMemo(
    () => `${csDiagnostics.charging_station?.[0]?.device_id || ''}|${csDiagnostics.start_date}|${csDiagnostics.end_date}`,
    [csDiagnostics.charging_station, csDiagnostics.start_date, csDiagnostics.end_date]
  );

  const clearResults = useCallback(() => {
    setCsInformation({});
    setCsLogCustomers([]);
    setCsLogData([]);
    setMetadataRows([]);
    setMetadataColumns([]);
    setMetadataPanelVisible(false);
  }, []);

  useEffect(() => {
    clearResults();
  }, [searchKey, clearResults]);

  const fetchData = async (params) => {
    try {
      setIsLoading(true);

      try {
        const info = await fetchCsInformation(params);
        setCsInformation(info ?? {});
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch CS information');
      }

      try {
        const customers = await fetchCsLogCustomer(params);
        setCsLogCustomers(customers);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch customer data');
      }

      try {
        const logs = await fetchUploadLog(params);
        setCsLogData(logs);
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to fetch log data');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSearchClick = () => {
    const model = new CsDiagnostics(csDiagnostics);
    try {
      model.completeValidation();
      setIsApiCalled(true);
      clearResults();
      fetchData(model.toApiJson());
    } catch (error) {
      setCsDiagnostics(model.toStateJson());
    }
  };

  const onMqttButtonClick = () => {
    clearResults();
    setShowModal(true);
  };

  const getValidatedParams = () => {
    const model = new CsDiagnostics(csDiagnostics);
    try {
      return model.toApiJson();
    } catch (error) {
      setCsDiagnostics(model.toStateJson());
      return null;
    }
  };

  const onLoadMetadataClick = async () => {
    const params = getValidatedParams();
    if (!params) return;

    try {
      setIsLoading(true);
      const response = await fetchMetadata(params);
      const data = response?.data?.data;
      setMetadataPanelVisible(true);

      if (data && data.length > 0) {
        setMetadataRows(data);
        setMetadataColumns(Array.from(new Set(data.flatMap((obj) => Object.keys(obj)))));
      } else {
        setMetadataRows([]);
        setMetadataColumns([]);
        toast.warning('Data not found or empty.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load metadata');
    } finally {
      setIsLoading(false);
    }
  };

  const onStartMetadataClick = async () => {
    const params = getValidatedParams();
    if (!params) return;

    try {
      setIsLoading(true);
      const response = await postStartMetadata(params);
      toast.success(response?.data?.message || 'Metadata started successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to start metadata');
    } finally {
      setIsLoading(false);
    }
  };

  const onStopMetadataClick = async () => {
    const params = getValidatedParams();
    if (!params) return;

    try {
      setIsLoading(true);
      const response = await postStopMetadata(params);
      toast.success(response?.data?.message || 'Metadata stopped successfully');
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to stop metadata');
    } finally {
      setIsLoading(false);
    }
  };

  const onMqttOkClick = async () => {
    let params;
    try {
      params = new CsDiagnostics(csDiagnostics).toApiJson();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : error?.message || 'Validation failed');
      setShowModal(false);
      return;
    }

    try {
      setIsLoading(true);
      clearResults();
      try {
        await postMqttTrigger(params);
      } catch (error) {
        toast.error(error?.response?.data?.message || error?.message || 'Something went wrong!');
      }
      await fetchData(params);
    } finally {
      setIsLoading(false);
      setIsApiCalled(false);
      setShowModal(false);
    }
  };

  const formDisabled = isLoading;

  return (
    <Page
      uiPage={uiManager.getUiPage(PAGES.ChargingStationDiagnosticsPage.name)}
      breadcrumbs={[{ label: 'Home', url: '/dashboard' }, { label: 'Charging Station Diagnostics' }]}
      className="charging-station-diagnostics-page"
    >
      <PageContentWrapper contentHeading="Charging Station Diagnostics">
        {isLoading && <Loading />}
        <div className="cs-diagnostics-toolbar mt-2">
          <form autoComplete="off">
            <div className="cs-diagnostics-toolbar__row">
              <FormPartial
                csDiagnostics={csDiagnostics}
                setCsDiagnostics={setCsDiagnostics}
                setIsLoading={setIsLoading}
              />
            </div>
            <div className="cs-diagnostics-toolbar__row mt-2 justify-content-end">
              <div className="cs-diagnostics-toolbar__actions">
                <button
                  type="button"
                  className="btn btn-voltup-theme cs-diagnostics-toolbar__btn"
                  onClick={onSearchClick}
                  disabled={formDisabled}
                >
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-voltup-theme cs-diagnostics-toolbar__btn"
                  onClick={onMqttButtonClick}
                  disabled={formDisabled || !(isApiCalled && csLogData.length === 0)}
                >
                  MQTT
                </button>
                {!metadataPanelVisible && (
                  <button
                    type="button"
                    className="btn btn-voltup-theme cs-diagnostics-toolbar__btn"
                    onClick={onLoadMetadataClick}
                  >
                    Load Metadata
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
        <div className="row g-3 mt-1">
          <ShowInfo
            csInformation={csInformation}
            csLogCustomers={csLogCustomers}
            csLogData={csLogData}
            csDiagnostics={csDiagnostics}
          />
        </div>
        <div className="row g-3 mt-2">
          <ShowSummaryData
            csDiagnostics={csDiagnostics}
            setIsLoading={setIsLoading}
            metadataRows={metadataRows}
            metadataColumns={metadataColumns}
            metadataPanelVisible={metadataPanelVisible}
            onLoadMetadata={onLoadMetadataClick}
            onStartMetadata={onStartMetadataClick}
            onStopMetadata={onStopMetadataClick}
          />
        </div>
        <MqttConfirmModal show={showModal} onHide={() => setShowModal(false)} onConfirm={onMqttOkClick} />
      </PageContentWrapper>
    </Page>
  );
};

export default IndexForm;
